import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SiGooglesheets, SiN8N, SiAirtable } from 'react-icons/si';
import { HiOutlineLink } from 'react-icons/hi2';
import cloudApi from '../../../services/cloudApi';
import ClaudeIntegrationCard from './ClaudeIntegrationCard';
import ChatGptIntegrationCard from './ChatGptIntegrationCard';
import McpIntegrationCard from './McpIntegrationCard';
import DeliveryHistoryModal from './DeliveryHistoryModal';
import WebhookSetupGuideModal from './WebhookSetupGuideModal';
import RichTooltip from './RichTooltip';
import CardSkeleton from './CardSkeleton';

const IntegrationsView = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testingGoogleSheets, setTestingGoogleSheets] = useState(false);
  const [toast, setToast] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Webhook state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [disconnectingWebhook, setDisconnectingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [historyModal, setHistoryModal] = useState({ isOpen: false, provider: null, label: null });

  const loadIntegrations = useCallback(async () => {
    try {
      const data = await cloudApi.getIntegrations();
      setIntegrations(data);
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  // Handle OAuth callback redirect params
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');

    if (connected === 'google_sheets') {
      setToast({ type: 'success', message: 'Google Sheets connected successfully!' });
      loadIntegrations();
      setSearchParams({}, { replace: true });
    } else if (error) {
      const messages = {
        google_sheets_denied: 'Google Sheets connection was denied.',
        google_sheets_failed: 'Failed to connect Google Sheets. Please try again.',
        missing_params: 'Connection failed due to missing parameters.',
        missing_drive_scope: 'Google Sheets access was not granted. On the Google consent screen, please check the box for "See, edit, create and delete only the specific Google Drive files..." and try again.',
      };
      // The missing-scope error is the actionable instructions the user needs to read carefully
      // before retrying — keep it on screen until they dismiss it.
      const duration = error === 'missing_drive_scope' ? 0 : 5000;
      setToast({ type: 'error', message: messages[error] || 'Connection failed.', duration });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, loadIntegrations]);

  // Auto-dismiss toast (per-toast duration; pass `duration: 0` to make it sticky until clicked)
  useEffect(() => {
    if (toast) {
      const ms = typeof toast.duration === 'number' ? toast.duration : 5000;
      if (ms <= 0) return;
      const timer = setTimeout(() => setToast(null), ms);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleConnectGoogleSheets = async () => {
    setConnecting(true);
    try {
      const authUrl = await cloudApi.getGoogleSheetsAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      setToast({ type: 'error', message: 'Failed to initiate Google Sheets connection.' });
      setConnecting(false);
    }
  };

  const handleDisconnectGoogleSheets = async () => {
    setDisconnecting(true);
    try {
      await cloudApi.disconnectGoogleSheets();
      setToast({ type: 'success', message: 'Google Sheets disconnected.' });
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to disconnect:', error);
      setToast({ type: 'error', message: 'Failed to disconnect Google Sheets.' });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveWebhook = async () => {
    if (!webhookUrl.trim()) return;
    setSavingWebhook(true);
    try {
      await cloudApi.saveWebhook(webhookUrl.trim());
      setToast({ type: 'success', message: 'Webhook connected successfully!' });
      setWebhookUrl('');
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to save webhook:', error);
      setToast({ type: 'error', message: error.message || 'Failed to save webhook. Ensure the URL uses HTTPS.' });
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleDisconnectWebhook = async () => {
    setDisconnectingWebhook(true);
    try {
      await cloudApi.deleteWebhook();
      setWebhookSecret(null);
      setShowSetupGuide(false);
      setToast({ type: 'success', message: 'Webhook disconnected.' });
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to disconnect webhook:', error);
      setToast({ type: 'error', message: 'Failed to disconnect webhook.' });
    } finally {
      setDisconnectingWebhook(false);
    }
  };

  const handleTestGoogleSheets = async () => {
    setTestingGoogleSheets(true);
    try {
      const result = await cloudApi.testGoogleSheets();
      setToast({ type: 'success', message: `Google Sheets connection works (${result.durationMs}ms)` });
      // Refresh integrations so any cleared error_message disappears from the banner.
      const fresh = await cloudApi.getIntegrations();
      setIntegrations(fresh);
    } catch (error) {
      console.error('Failed to test Google Sheets:', error);
      setToast({ type: 'error', message: error.message || 'Google Sheets test failed.' });
    } finally {
      setTestingGoogleSheets(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    try {
      const result = await cloudApi.testWebhook();
      if (result.success) {
        setToast({ type: 'success', message: `Test payload delivered successfully (${result.statusCode}, ${result.responseTime}ms)` });
      } else {
        setToast({ type: 'error', message: `Webhook responded with status ${result.statusCode}` });
      }
    } catch (error) {
      console.error('Failed to test webhook:', error);
      setToast({ type: 'error', message: error.message || 'Failed to deliver test payload.' });
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleRevealSecret = async () => {
    setLoadingSecret(true);
    try {
      const result = await cloudApi.getWebhookSecret();
      setWebhookSecret(result.signingSecret);
    } catch (error) {
      console.error('Failed to get signing secret:', error);
      setToast({ type: 'error', message: 'Failed to load signing secret.' });
    } finally {
      setLoadingSecret(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    });
  };

  const googleSheets = integrations.find(i => i.provider === 'google_sheets');
  const isConnected = googleSheets?.status === 'active';
  const requiresReauth = googleSheets?.requires_reauth;
  const googleSheetsErrorMessage = googleSheets?.error_message;

  const webhook = integrations.find(i => i.provider === 'webhook');
  const isWebhookConnected = webhook?.status === 'active';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="px-1">
        <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
          Integrations
        </h1>
      </div>

      {/* Toast notification */}
      {toast && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium backdrop-blur-xl shadow-sm ${
          toast.type === 'success'
            ? 'bg-green-50/90 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200/60 dark:border-green-700/30'
            : 'bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30'
        }`}>
          {toast.type === 'success' ? (
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-auto text-current opacity-50 hover:opacity-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Claude — connect (sign-in based) and disconnect, managed right here. */}
        <ClaudeIntegrationCard />

        {/* ChatGPT — same sign-in story as Claude, so it gets the same treatment. */}
        <ChatGptIntegrationCard />

        {/* MCP — every other AI agent (Claude Code, Cursor, any MCP client):
            token create/revoke plus the per-client setup guide. */}
        <McpIntegrationCard />

        {/* Webhook + Google Sheets are the only cards that need getIntegrations,
            so they are the only ones that wait for it. */}
        {loading ? <CardSkeleton rows={2} /> : (
        <div className="bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl border border-gray-200/60 dark:border-gray-700/40 shadow-lg shadow-gray-200/40 dark:shadow-black/20 p-6 flex flex-col transition-all duration-300 hover:shadow-xl">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-800/60 dark:to-gray-900/40 shadow-md shadow-gray-200/30 dark:shadow-black/20 ring-1 ring-gray-200/80 dark:ring-gray-700/60 flex items-center justify-center">
                <HiOutlineLink className="w-7 h-7 text-[#6366F1]" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Webhook</h3>
                  <RichTooltip
                    content={
                      <div className="space-y-2.5 text-xs">
                        <p className="text-sm font-semibold text-white">How Webhooks Work</p>
                        <div className="space-y-2 text-gray-300 leading-relaxed">
                          <div className="flex gap-2">
                            <span className="text-yellow-400 font-bold flex-shrink-0">1.</span>
                            <span>Enable the webhook in your automation settings</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="text-yellow-400 font-bold flex-shrink-0">2.</span>
                            <span>Your automation runs and extracts data</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="text-yellow-400 font-bold flex-shrink-0">3.</span>
                            <span>We POST the results to your URL with a download link</span>
                          </div>
                        </div>
                        <p className="text-gray-400 text-2xs pt-1.5 border-t border-gray-700">
                          Must be enabled per automation. Includes HMAC signature for verification.
                        </p>
                      </div>
                    }
                    width={300}
                  >
                    <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                    </svg>
                  </RichTooltip>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Send extracted data to your endpoint</p>
              </div>
            </div>

            {isWebhookConnected && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                Connected
              </span>
            )}
          </div>

          {isWebhookConnected ? (
            <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
              <div className="space-y-1 mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Sending to <span className="font-medium text-gray-700 dark:text-gray-200 font-mono text-xs break-all">{webhook.metadata?.url}</span>
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                  Data is posted to this URL each time an automation completes (configurable per automation).
                </p>
                {webhook.last_used_at && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                    Last used {new Date(webhook.last_used_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div className="rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.04]">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] text-gray-600 dark:text-gray-400 select-none">
                      Export format
                    </span>
                    <RichTooltip
                      content={
                        <div className="space-y-2 text-xs">
                          <p className="text-sm font-semibold text-white">Export Format</p>
                          <p className="text-gray-300 leading-relaxed">
                            Choose the file format for data sent to your webhook. CSV is a flat table format, JSON is structured key-value data.
                          </p>
                        </div>
                      }
                      width={260}
                    >
                      <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                      </svg>
                    </RichTooltip>
                  </div>
                  <div className="flex rounded-full bg-gray-200/70 dark:bg-white/[0.06] p-0.5">
                    {['csv', 'json'].map((fmt) => {
                      const selected = (webhook.metadata?.exportFormat || 'csv') === fmt;
                      return (
                        <button
                          key={fmt}
                          onClick={async () => {
                            if (selected) return;
                            setIntegrations(prev => prev.map(i =>
                              i.provider === 'webhook'
                                ? { ...i, metadata: { ...i.metadata, exportFormat: fmt } }
                                : i
                            ));
                            try {
                              await cloudApi.updateWebhookSettings({ exportFormat: fmt });
                            } catch (error) {
                              console.error('Failed to update webhook format:', error);
                              setIntegrations(prev => prev.map(i =>
                                i.provider === 'webhook'
                                  ? { ...i, metadata: { ...i.metadata, exportFormat: fmt === 'csv' ? 'json' : 'csv' } }
                                  : i
                              ));
                            }
                          }}
                          className={`px-3 py-1 text-xs font-medium uppercase rounded-full transition-all duration-200 ${
                            selected
                              ? 'bg-white dark:bg-white/[0.12] text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                          }`}
                        >
                          {fmt}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] text-gray-600 dark:text-gray-400 select-none">
                      Send data
                    </span>
                    <RichTooltip
                      content={
                        <div className="space-y-2 text-xs">
                          <p className="text-sm font-semibold text-white">Send Condition</p>
                          <p className="text-gray-300 leading-relaxed">
                            Control when data is sent to your webhook. "Always" sends data after every run. "Only on success" skips delivery when a run fails or is stopped.
                          </p>
                        </div>
                      }
                      width={260}
                    >
                      <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                      </svg>
                    </RichTooltip>
                  </div>
                  <div className="flex rounded-full bg-gray-200/70 dark:bg-white/[0.06] p-0.5">
                    {[{ value: 'always', label: 'Always' }, { value: 'on_success', label: 'On success' }].map(({ value, label }) => {
                      const selected = (webhook.metadata?.sendCondition || 'always') === value;
                      return (
                        <button
                          key={value}
                          onClick={async () => {
                            if (selected) return;
                            const prev = webhook.metadata?.sendCondition || 'always';
                            setIntegrations(p => p.map(i =>
                              i.provider === 'webhook'
                                ? { ...i, metadata: { ...i.metadata, sendCondition: value } }
                                : i
                            ));
                            try {
                              await cloudApi.updateWebhookSettings({ sendCondition: value });
                            } catch (error) {
                              console.error('Failed to update send condition:', error);
                              setIntegrations(p => p.map(i =>
                                i.provider === 'webhook'
                                  ? { ...i, metadata: { ...i.metadata, sendCondition: prev } }
                                  : i
                              ));
                            }
                          }}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                            selected
                              ? 'bg-white dark:bg-white/[0.12] text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-auto pt-3 flex-wrap">
                <button
                  onClick={handleTestWebhook}
                  disabled={testingWebhook}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 rounded-lg transition-all duration-200 disabled:opacity-50"
                >
                  {testingWebhook ? (
                    <span className="flex items-center gap-1.5">
                      <span className="w-3.5 h-3.5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin"></span>
                      Testing...
                    </span>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Send Test
                    </>
                  )}
                </button>
                <span className="w-px h-4 bg-gray-200 dark:bg-gray-700/50 hidden xs:block"></span>
                <button
                  onClick={() => setShowSetupGuide(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Setup Guide
                </button>
                <span className="w-px h-4 bg-gray-200 dark:bg-gray-700/50 hidden xs:block"></span>
                <button
                  onClick={() => setHistoryModal({ isOpen: true, provider: 'webhook', label: 'Webhook' })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activity
                </button>
                <span className="w-px h-4 bg-gray-200 dark:bg-gray-700/50 hidden xs:block"></span>
                <button
                  onClick={handleDisconnectWebhook}
                  disabled={disconnectingWebhook}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-all duration-200 disabled:opacity-50"
                >
                  {disconnectingWebhook ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          ) : (
            <div className="border-t border-gray-200 dark:border-gray-700/30 pt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Data will be posted to this URL each time an automation completes (configurable per automation). Connect to Zapier, Make, n8n, or your own server.
              </p>
              <div className="flex flex-col xs:flex-row gap-2">
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-server.com/webhook"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveWebhook()}
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700/50 rounded-xl
                             bg-white dark:bg-gray-800/50 text-gray-700 dark:text-gray-200
                             placeholder-gray-400 dark:placeholder-gray-500
                             focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none"
                />
                <button
                  onClick={handleSaveWebhook}
                  disabled={savingWebhook || !webhookUrl.trim()}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 font-medium text-sm rounded-xl border border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15] transition-all duration-200 disabled:opacity-50 w-full xs:w-auto"
                >
                  {savingWebhook ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Saving...
                    </span>
                  ) : (
                    'Connect'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Google Sheets Integration */}
        {loading ? <CardSkeleton rows={2} /> : (
        <div className="bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl border border-gray-200/60 dark:border-gray-700/40 shadow-lg shadow-gray-200/40 dark:shadow-black/20 p-6 flex flex-col transition-all duration-300 hover:shadow-xl">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-800/60 dark:to-gray-900/40 shadow-md shadow-gray-200/30 dark:shadow-black/20 ring-1 ring-gray-200/80 dark:ring-gray-700/60 flex items-center justify-center">
                <SiGooglesheets className="w-7 h-7 text-[#0F9D58]" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Google Sheets</h3>
                  <RichTooltip
                    content={
                      <div className="space-y-2.5 text-xs">
                        <p className="text-sm font-semibold text-white">How Google Sheets Works</p>
                        <div className="space-y-2 text-gray-300 leading-relaxed">
                          <div className="flex gap-2">
                            <span className="text-emerald-400 font-bold flex-shrink-0">1.</span>
                            <span>Enable Google Sheets in your automation settings</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="text-emerald-400 font-bold flex-shrink-0">2.</span>
                            <span>Your automation runs and extracts data</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="text-emerald-400 font-bold flex-shrink-0">3.</span>
                            <span>Results are exported to a new Google Sheets spreadsheet</span>
                          </div>
                        </div>
                        <p className="text-gray-400 text-2xs pt-1.5 border-t border-gray-700">
                          Must be enabled per automation.
                        </p>
                      </div>
                    }
                    width={300}
                  >
                    <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                    </svg>
                  </RichTooltip>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Export extracted data to Google Sheets</p>
              </div>
            </div>

            {isConnected && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                Connected
              </span>
            )}
          </div>

          {isConnected ? (
            <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
              {googleSheetsErrorMessage && (
                <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                  </svg>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Last export failed: {googleSheetsErrorMessage} If exports keep failing, click Disconnect below and reconnect.
                  </p>
                </div>
              )}
              {requiresReauth && (
                <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                  </svg>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Permissions have changed. Please disconnect and reconnect to update.
                  </p>
                </div>
              )}

              <div className="space-y-1 mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  Connected as <span className="font-medium text-gray-700 dark:text-gray-200">{googleSheets.account_email}</span>
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                  Creates a new spreadsheet per completed automation. Enable per automation in its settings.
                </p>
                {googleSheets.last_used_at && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                    Last used {new Date(googleSheets.last_used_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div className="rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.04]">
                <label className="flex items-center justify-between px-4 py-3 cursor-pointer group">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] text-gray-600 dark:text-gray-400 select-none group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors">
                      Inline images
                    </span>
                    <RichTooltip
                      content={
                        <div className="space-y-2 text-xs">
                          <p className="text-sm font-semibold text-white">Inline Images</p>
                          <p className="text-gray-300 leading-relaxed">
                            Render image URLs as visible thumbnails in Google Sheets cells instead of plain text.
                          </p>
                          <p className="text-gray-400 text-2xs pt-1.5 border-t border-gray-700">
                            Uses the Google Sheets{" "}
                            <a href="https://support.google.com/docs/answer/3093333" target="_blank" rel="noopener noreferrer"
                               className="text-emerald-400 hover:underline pointer-events-auto">
                              IMAGE function
                            </a>
                          </p>
                        </div>
                      }
                      width={260}
                    >
                      <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                      </svg>
                    </RichTooltip>
                  </div>
                  <input
                    type="checkbox"
                    checked={googleSheets.metadata?.inlineImages === true}
                    onChange={async (e) => {
                      const checked = e.target.checked;
                      setIntegrations(prev => prev.map(i =>
                        i.provider === 'google_sheets'
                          ? { ...i, metadata: { ...i.metadata, inlineImages: checked } }
                          : i
                      ));
                      try {
                        await cloudApi.updateGoogleSheetsSettings({ inlineImages: checked });
                      } catch (error) {
                        console.error('Failed to update setting:', error);
                        setIntegrations(prev => prev.map(i =>
                          i.provider === 'google_sheets'
                            ? { ...i, metadata: { ...i.metadata, inlineImages: !checked } }
                            : i
                        ));
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="relative w-8 h-[18px] rounded-full bg-gray-300 dark:bg-white/[0.1] peer-checked:bg-emerald-500 dark:peer-checked:bg-emerald-500 transition-colors duration-200 flex-shrink-0 after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:w-3 after:h-3 after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:duration-200 peer-checked:after:translate-x-3.5 peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500/40"></div>
                </label>

                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] text-gray-600 dark:text-gray-400 select-none">
                      Send data
                    </span>
                    <RichTooltip
                      content={
                        <div className="space-y-2 text-xs">
                          <p className="text-sm font-semibold text-white">Send Condition</p>
                          <p className="text-gray-300 leading-relaxed">
                            Control when data is exported to Google Sheets. "Always" exports after every run. "Only on success" skips export when a run fails or is stopped.
                          </p>
                        </div>
                      }
                      width={260}
                    >
                      <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                      </svg>
                    </RichTooltip>
                  </div>
                  <div className="flex rounded-full bg-gray-200/70 dark:bg-white/[0.06] p-0.5">
                    {[{ value: 'always', label: 'Always' }, { value: 'on_success', label: 'On success' }].map(({ value, label }) => {
                      const selected = (googleSheets.metadata?.sendCondition || 'always') === value;
                      return (
                        <button
                          key={value}
                          onClick={async () => {
                            if (selected) return;
                            const prev = googleSheets.metadata?.sendCondition || 'always';
                            setIntegrations(p => p.map(i =>
                              i.provider === 'google_sheets'
                                ? { ...i, metadata: { ...i.metadata, sendCondition: value } }
                                : i
                            ));
                            try {
                              await cloudApi.updateGoogleSheetsSettings({ sendCondition: value });
                            } catch (error) {
                              console.error('Failed to update send condition:', error);
                              setIntegrations(p => p.map(i =>
                                i.provider === 'google_sheets'
                                  ? { ...i, metadata: { ...i.metadata, sendCondition: prev } }
                                  : i
                              ));
                            }
                          }}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                            selected
                              ? 'bg-white dark:bg-white/[0.12] text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-auto pt-3 flex-wrap">
                <button
                  onClick={() => setHistoryModal({ isOpen: true, provider: 'google_sheets', label: 'Google Sheets' })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activity
                </button>
                <span className="w-px h-4 bg-gray-200 dark:bg-gray-700/50 hidden xs:block"></span>
                <button
                  onClick={handleTestGoogleSheets}
                  disabled={testingGoogleSheets}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 rounded-lg transition-all duration-200 disabled:opacity-50"
                >
                  {testingGoogleSheets ? (
                    <span className="flex items-center gap-1.5">
                      <span className="w-3.5 h-3.5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin"></span>
                      Testing...
                    </span>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Test
                    </>
                  )}
                </button>
                <span className="w-px h-4 bg-gray-200 dark:bg-gray-700/50 hidden xs:block"></span>
                <button
                  onClick={handleDisconnectGoogleSheets}
                  disabled={disconnecting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-all duration-200 disabled:opacity-50"
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
              {googleSheetsErrorMessage && (
                <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                  </svg>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {googleSheetsErrorMessage}
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                Connect your Google account to automatically export extracted data to new spreadsheets.
              </p>
              <div className="mt-auto">
                <button
                  onClick={handleConnectGoogleSheets}
                  disabled={connecting}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 font-medium text-sm rounded-xl border border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15] transition-all duration-200 disabled:opacity-50"
                >
                  {connecting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Connecting...
                    </span>
                  ) : (
                    'Connect Google Sheets'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* n8n — coming soon */}
        <div className="bg-white/40 dark:bg-gray-900/25 backdrop-blur-xl rounded-xl border border-dashed border-gray-300/60 dark:border-gray-600/40 p-6 flex flex-col transition-all duration-300">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-800/60 dark:to-gray-900/40 shadow-sm ring-1 ring-gray-200/80 dark:ring-gray-700/60 flex items-center justify-center">
                <SiN8N className="w-7 h-7 text-[#EA4B71] opacity-50" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">n8n</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Automate workflows with n8n</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200/50 dark:border-gray-700/20 pt-4">
            <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mb-3">
              Native n8n node for seamless workflow automation — trigger actions, transform data, and connect to 400+ services.
            </p>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-900/15 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-700/30">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Coming soon
            </span>
          </div>
        </div>

        {/* Airtable — coming soon */}
        <div className="bg-white/40 dark:bg-gray-900/25 backdrop-blur-xl rounded-xl border border-dashed border-gray-300/60 dark:border-gray-600/40 p-6 flex flex-col transition-all duration-300">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-800/60 dark:to-gray-900/40 shadow-sm ring-1 ring-gray-200/80 dark:ring-gray-700/60 flex items-center justify-center">
                <SiAirtable className="w-7 h-7 text-[#18BFFF] opacity-50" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Airtable</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Export data to Airtable bases</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200/50 dark:border-gray-700/20 pt-4">
            <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mb-3">
              Sync extracted data directly to Airtable bases — organize, filter, and collaborate on your data in real time.
            </p>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-900/15 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-700/30">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Coming soon
            </span>
          </div>
        </div>
      </div>

      {/* Nominative use, not partnership. Every third-party mark we render needs
          naming here. */}
      <p className="px-1 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
        Claude is a trademark of Anthropic, PBC. ChatGPT is a trademark of OpenAI, L.L.C. Ultimate Web
        Scraper is an independent product and is not affiliated with, endorsed by, or sponsored by
        Anthropic or OpenAI.
      </p>

      <DeliveryHistoryModal
        provider={historyModal.provider}
        providerLabel={historyModal.label}
        isOpen={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false, provider: null, label: null })}
      />

      <WebhookSetupGuideModal
        isOpen={showSetupGuide}
        onClose={() => { setShowSetupGuide(false); setWebhookSecret(null); }}
        webhookSecret={webhookSecret}
        onRevealSecret={handleRevealSecret}
        loadingSecret={loadingSecret}
        onCopySecret={copyToClipboard}
        secretCopied={secretCopied}
      />
    </div>
  );
};

export default IntegrationsView;
